import { useState } from 'react';
import QuoteSimulator from './QuoteSimulator';
import type { SimulatorQuoteData } from '../page';

interface PricingFactorsProps {
  onSimulatorQuote?: (data: SimulatorQuoteData) => void;
}

export default function PricingFactors({ onSimulatorQuote }: PricingFactorsProps) {
  const [showSimulator, setShowSimulator] = useState(false);

  const handleConsult = (data: SimulatorQuoteData) => {
    setShowSimulator(false);
    if (onSimulatorQuote) {
      onSimulatorQuote(data);
    }
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <section id="technology" className="relative py-16 sm:py-28 px-4 sm:px-6 overflow-hidden" style={{background: 'linear-gradient(140deg, #152a52 0%, #0d1f3c 50%, #111e3a 100%)'}}>
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(118deg, transparent 44%, #c9a227 45%, #c9a227 45.4%, transparent 46%), linear-gradient(72deg, transparent 60%, #c9a227 61%, #c9a227 61.3%, transparent 62%)', backgroundSize: '340px 340px'}}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="rounded-3xl p-8 sm:p-14 border border-[#c9a227]/20 shadow-sm" style={{background: 'linear-gradient(160deg, rgba(30,58,110,0.6), rgba(13,31,60,0.8))'}}>
          <div className="text-center mb-10 sm:mb-14">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-5 leading-[1.2] tracking-tight">
              <span className="text-[#c9a227]">21개</span> 투명 요인 기반 정찰제
            </h3>
            <p className="text-base sm:text-xl text-white/65 leading-relaxed">
              불투명한 '묻지마 금액'은 이제 그만
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 sm:gap-4">
            {[
              { icon: 'ri-ruler-line', title: '초고(草高)', desc: '풀 높이 측정', tooltip: '풀의 높이에 따라 작업 난이도와 소요 시간이 달라집니다. 허리 이상의 잡풀은 추가 할증이 적용됩니다.' },
              { icon: 'ri-plant-line', title: '식생 분포', desc: '잡목 종류 파악', tooltip: '잡목·가시나무·칙칙 등 식생 종류에 따라 필요한 장비와 인력이 결정됩니다. 엔진톱 투입 여부도 이 요인으로 판단합니다.' },
              { icon: 'ri-arrow-up-line', title: '경사도', desc: '지형 난이도', tooltip: '급경사 지형은 장비 운용과 안전 확보에 추가 비용이 발생합니다. 경사각 30° 이상 시 안전 할증이 적용됩니다.' },
              { icon: 'ri-map-pin-2-line', title: '접근성', desc: '작업지 거리', tooltip: '차량 진입 불가 구간이 300m 이상일 경우 장비 운반 인력비가 추가됩니다. GPS로 정확한 거리를 측정합니다.' },
              { icon: 'ri-landscape-line', title: '면적', desc: 'GPS 정밀 측정', tooltip: 'GPS 기반 정밀 측정으로 ±3% 오차 범위 내 면적을 산출합니다. 20평 초과 시 평당 3,000원이 추가됩니다.' },
              { icon: 'ri-time-line', title: '작업 시간', desc: '예상 소요 시간', tooltip: '위 5가지 요인을 종합해 예상 작업 시간을 산출합니다. 시간당 인건비와 장비 사용료가 최종 견적에 반영됩니다.' },
              { icon: 'ri-cloud-windy-line', title: '기상 조건', desc: '날씨·계절 반영', tooltip: '작업 당일 기상 상태(강풍·폭염·우천)에 따라 안전 작업 가능 여부와 소요 시간이 달라집니다. 기상청 API를 실시간 연동합니다.' },
              { icon: 'ri-user-line', title: '투입 인원', desc: '작업자 수 산정', tooltip: '면적·난이도·시간 제약을 고려해 최적 투입 인원을 산정합니다. 인원 수에 따라 인건비가 정확히 계산됩니다.' },
              { icon: 'ri-tools-line', title: '장비 종류', desc: '필요 장비 선정', tooltip: '예초기·엔진톱·고압세척기 등 현장에 필요한 장비를 사전에 파악합니다. 장비 종류별 사용료가 견적에 반영됩니다.' },
              { icon: 'ri-truck-line', title: '이동 거리', desc: '출장 거리 계산', tooltip: '작업지까지의 이동 거리에 따라 출장비가 산정됩니다. 50km 이내 기본 포함, 초과 시 km당 추가 비용이 적용됩니다.' },
              { icon: 'ri-recycle-line', title: '폐기물량', desc: '잔재물 처리', tooltip: '벌초·정원 작업 후 발생하는 잔재물(풀·나뭇가지 등)의 양을 예측합니다. 처리량에 따라 폐기물 처리비가 산정됩니다.' },
              { icon: 'ri-shield-check-line', title: '안전 등급', desc: '위험도 평가', tooltip: '작업 현장의 위험 요소(절벽·독충·전선 등)를 사전 평가합니다. 위험 등급에 따라 안전 장비 비용과 보험료가 반영됩니다.' },
              { icon: 'ri-calendar-line', title: '작업 시기', desc: '성수기 여부', tooltip: '추석·설 전후 성수기에는 수요 집중으로 일정 조율이 필요합니다. 성수기 예약 시 조기 예약 할인 혜택을 제공합니다.' },
              { icon: 'ri-water-flash-line', title: '토양 상태', desc: '습도·배수 조건', tooltip: '토양 습도와 배수 상태에 따라 장비 진입 가능 여부가 결정됩니다. 과습 지역은 장비 손상 방지를 위한 추가 조치가 필요합니다.' },
              { icon: 'ri-building-line', title: '구조물 유무', desc: '비석·상석 등', tooltip: '비석·상석·봉분 등 구조물 주변 작업은 세심한 수작업이 필요합니다. 구조물 수와 크기에 따라 추가 작업 시간이 산정됩니다.' },
              { icon: 'ri-bug-line', title: '병해충 여부', desc: '해충·독초 확인', tooltip: '말벌집·독사·독초 등 위험 요소를 사전에 파악합니다. 해충 제거 작업이 필요한 경우 별도 방제 비용이 추가됩니다.' },
              { icon: 'ri-sun-line', title: '일조 환경', desc: '그늘·햇빛 조건', tooltip: '그늘진 환경은 이끼·습기로 인해 작업 난이도가 높아집니다. 일조 환경에 따라 작업 방식과 소요 시간이 달라집니다.' },
              { icon: 'ri-group-line', title: '묘지 기수', desc: '합장·단독 구분', tooltip: '단독묘·합장묘·문중 묘역 등 형태에 따라 작업 범위가 달라집니다. 기수가 많을수록 문중 묶음 할인이 적용됩니다.' },
              { icon: 'ri-history-line', title: '관리 이력', desc: '최근 관리 시점', tooltip: '마지막 관리 시점이 오래될수록 잡풀과 잡목이 더 많이 자라 작업량이 증가합니다. 관리 이력에 따라 초기 정비 비용이 산정됩니다.' },
              { icon: 'ri-camera-line', title: '사진 기록', desc: '전후 비교 촬영', tooltip: '작업 전·중·후 사진을 체계적으로 기록합니다. 드론 촬영 포함 여부에 따라 기록 서비스 비용이 달라집니다.' },
              { icon: 'ri-file-list-3-line', title: '추가 서비스', desc: '제초제·비료 등', tooltip: '제초제 살포·비료 시비·잔디 보식 등 추가 서비스를 선택할 수 있습니다. 선택한 서비스에 따라 자재비와 작업비가 추가됩니다.' },
            ].map((factor, index) => (
              <div key={index} className="relative group">
                <div className="rounded-xl p-4 sm:p-5 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-1 border border-[#c9a227]/10 hover:border-[#c9a227]/30 cursor-default" style={{background: 'rgba(30,58,110,0.4)'}}>
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2.5">
                    <i className={`${factor.icon} text-3xl text-[#c9a227]`}></i>
                  </div>
                  <h4 className="text-sm font-bold text-white mb-1.5 tracking-tight leading-snug">{factor.title}</h4>
                  <p className="text-xs text-white/50 leading-relaxed">{factor.desc}</p>
                </div>
                {/* 툴팁 */}
                <div className={`absolute ${index < 7 ? 'top-full mt-3 bottom-auto' : 'bottom-full mb-3 top-auto'} left-1/2 -translate-x-1/2 w-56 bg-[#0d1f3c] text-white text-xs rounded-xl px-4 py-3 shadow-xl border border-[#c9a227]/20
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible
                  transition-all duration-200 ease-out
                  pointer-events-none z-50
                  leading-relaxed text-center`}>
                  {factor.tooltip}
                  {index < 7 ? (
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-[#0d1f3c]"></span>
                  ) : (
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0d1f3c]"></span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 sm:mt-12 flex justify-center">
            <button
              onClick={() => setShowSimulator(true)}
              className="relative inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold cursor-pointer whitespace-nowrap select-none text-[#0d1f3c] tracking-wide transition-all duration-100 hover:opacity-90 hover:translate-y-[2px] active:translate-y-[4px] text-base"
              style={{
                background: 'linear-gradient(135deg, #e8c84a, #b8860b)',
                boxShadow: '0 6px 0 #7a5c06, 0 8px 12px rgba(0,0,0,0.4)',
              }}
            >
              <i className="ri-map-pin-line text-base"></i>
              고객이 미리 확인하는 견적 산출
            </button>
          </div>
        </div>
      </div>

      <QuoteSimulator isOpen={showSimulator} onClose={() => setShowSimulator(false)} onConsult={handleConsult} />
    </section>
  );
}
